import { Type } from 'arktype';

/**
 * A branded version of the base `string` type that can be used to represent
 * any string value while allowing for stricter typing in certain contexts.
 *
 * This pattern (`string & {}`) ensures the type is distinct enough to prevent
 * unintended structural compatibility with other branded string types,
 * while still behaving like a regular string at runtime.
 *
 * @example
 * let value: any_string = "Hello, world!";
 * // still usable anywhere a string is expected
 * console.log(value.toUpperCase());
 *
 * // stringsType is not simplified to just string
 * type stringsType = any_string | `special:${string}`
 */
export type any_string = string & {};

/**
 * A branded type used to create nominal typing in TypeScript.
 *
 * This type wraps another type `Type` and adds a `__brand` property,
 * which is used to differentiate this type from other types structurally
 * identical to `Type`. This helps enforce stronger type safety by preventing
 * accidental mixing of types that share the same shape but represent different concepts.
 *
 * @template Type The underlying type to be branded.
 *
 * @example
 * type A = number & Brand<'A'>;
 * type B = number & Brand<'B'>;
 * const a = 1 as A;
 * const b: B = a; // <--- this is an error
 */
export type Brand<Type> = { __brand: Type };

export function isCallable(value: any): value is Function {
  return typeof value === 'function';
}
